import React, { FC, ChangeEvent, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { compose } from 'recompose';
import { uniq } from 'lodash';
import { connect, FormikProps, FormikValues } from 'formik';
import { Divider, Grid, Typography } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import GroupAddIcon from '@material-ui/icons/GroupAdd';
import SettingsApplicationsIcon from '@material-ui/icons/SettingsApplications';

import { ValidatedTextField } from 'components';

interface IPropsEnhanced extends WithStyles<typeof styles> {
  formik: FormikProps<FormikValues>;
}

const styles = (theme: Theme) =>
  createStyles({
    divider: {
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(3),
    },
    label: {
      marginBottom: -theme.spacing(1) * 3,
    },
    gutterBottom: {
      marginBottom: theme.spacing(3),
    },
    secondary: {
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.grey[400]}`,
      borderRadius: 4,
    },
  });

const enhancers = compose<IPropsEnhanced, {}>(connect, withStyles(styles));

const Form: FC<IPropsEnhanced> = ({ classes, formik }) => {
  const { t } = useTranslation();

  function handleTextAreaChange(event: ChangeEvent<HTMLTextAreaElement>) {
    const { value } = event.target;
    const emailAddresses = value.split(',').map(email => email.trim());

    formik.setFieldValue('email_addresses', uniq(emailAddresses));
  }

  function handleValue(value: string[]) {
    return value.join(', ');
  }

  return (
    <Fragment>
      {!formik.values.uploadfile && (
        <Fragment>
          <Grid container>
            <Grid item xs={3}>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <GroupAddIcon color="disabled" fontSize="large" />
                </Grid>
                <Grid item>
                  <Typography variant="subtitle2" color="textPrimary">
                    {t('messages:recipients')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={7}>
              <ValidatedTextField
                name="email_addresses"
                i18nKey="admin:emails"
                valueFn={handleValue}
                color="primary"
                TextFieldProps={{
                  multiline: true,
                  rows: 8,
                  onChange: handleTextAreaChange,
                  label: null,
                  margin: 'none',
                }}
              />
            </Grid>
          </Grid>
          <Divider className={classes.divider} />
        </Fragment>
      )}

      <Grid container>
        <Grid item xs={3}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <SettingsApplicationsIcon color="disabled" fontSize="large" />
            </Grid>
            <Grid item>
              <Typography variant="subtitle2" color="textPrimary">
                {t('common:options')}
              </Typography>
              <Typography variant="subtitle2" color="textPrimary">
                {t('components:not_required')}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={7}>
          <Typography variant="subtitle2" color="textPrimary" className={classes.label}>
            {t('admin:from')}
          </Typography>
          <div className={classes.gutterBottom}>
            <ValidatedTextField
              name="email_from_address"
              i18nKey="admin:enterValidEmail"
              color="primary"
            />
          </div>
          <Typography variant="subtitle2" color="textPrimary" className={classes.label}>
            {t('admin:subject')}
          </Typography>
          <div className={classes.gutterBottom}>
            <ValidatedTextField color="primary" name="subject" i18nKey="admin:addASubjectLine" />
          </div>
          <Typography variant="subtitle2" color="textPrimary" className={classes.label}>
            {t('admin:message')}
          </Typography>
          <ValidatedTextField
            color="primary"
            name="message"
            i18nKey="admin:addACustomEmailMessage"
            TextFieldProps={{ multiline: true, rows: 8 }}
          />
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default enhancers(Form);
